import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../../environments/environment';
import { AnalyticsConsentService } from '../analytics-consent/analytics-consent.service';
import { ClientStore } from '../../stores/client/client.store';

declare global {
  interface Window {
    clarity?: (command: string, ...args: unknown[]) => void;
  }
}

/**
 * Service for integrating Microsoft Clarity analytics.
 * Clarity provides session recordings, heatmaps, and user insights.
 *
 * Note: This service only initializes Clarity on web platforms.
 * For native iOS/Android apps, use the native Clarity SDKs instead.
 */
@Injectable({
  providedIn: 'root',
})
export class ClarityService {
  private readonly clientStore = inject(ClientStore);
  private readonly analyticsConsentService = inject(AnalyticsConsentService);
  private isInitialized = false;
  private readonly MAX_RETRY_ATTEMPTS = 10;
  private retryCount = 0;

  /**
   * Initializes Microsoft Clarity analytics.
   * Only runs on web platform when user has explicitly consented.
   * Native apps should use native Clarity SDKs instead.
   */
  initialise(): void {
    // Skip on native platforms - use native Clarity SDKs instead
    if (Capacitor.isNativePlatform()) {
      return;
    }

    // Only initialise if user has explicitly allowed analytics
    if (!this.analyticsConsentService.isAllowed()) {
      return;
    }

    const projectId = environment.clarity?.projectId;
    if (!projectId) {
      console.warn('[Clarity] Microsoft Clarity is not configured (clarity.projectId not set)');
      return;
    }

    if (this.isInitialized) {
      this.setCustomSessionData();
      return;
    }

    this.injectClarityScript(projectId);
    this.isInitialized = true;
    this.setCustomSessionData();
  }

  /**
   * Injects the Clarity tracking script into the document head.
   */
  private injectClarityScript(projectId: string): void {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${projectId}");
    `;
    document.head.appendChild(script);
  }

  /**
   * Sets custom session data to identify users and context in Clarity dashboard.
   * Retries if Clarity script hasn't loaded yet.
   */
  private setCustomSessionData(): void {
    if (typeof window.clarity === 'function') {
      const client = this.clientStore.client();
      const personalDetails = this.clientStore.personalDetails();

      // Identify this as HomeBuyer app (distinguishes from SmartrWeb/monolith)
      window.clarity('set', 'app', 'homebuyer');

      if (client?.clientId) {
        window.clarity('set', 'clientId', String(client.clientId));
      }

      if (personalDetails?.Email) {
        window.clarity('set', 'user', personalDetails.Email);
      }

      // Set environment identifier
      const envName = this.getEnvironmentName();
      if (envName) {
        window.clarity('set', 'environment', envName);
      }

      this.retryCount = 0;
    } else {
      if (this.retryCount < this.MAX_RETRY_ATTEMPTS) {
        this.retryCount++;
        setTimeout(() => this.setCustomSessionData(), 1000);
      } else {
        console.warn(`[Clarity] Failed to set custom data after ${this.MAX_RETRY_ATTEMPTS} attempts`);
      }
    }
  }

  /**
   * Determines the environment name from the web app domain.
   */
  private getEnvironmentName(): string {
    const domain = environment.webAppDomain;
    if (!domain) return 'unknown';

    if (domain.includes('.dev.')) return 'dev';
    if (domain.includes('.release.')) return 'release';
    if (domain.includes('homebuyer.smartr365.com')) return 'production';
    return 'local';
  }

  /**
   * Disables Clarity tracking (e.g., when user revokes consent).
   */
  disable(): void {
    if (typeof window.clarity === 'function') {
      window.clarity('consent', false);
    }
    this.isInitialized = false;
  }

  /**
   * Updates custom session data when user information changes.
   * Call this after login or when client data is loaded.
   */
  updateSessionData(): void {
    if (this.isInitialized) {
      this.retryCount = 0;
      this.setCustomSessionData();
    }
  }
}
